/* eslint-disable react/prop-types, prettier/prettier */
import { keyframes } from '@emotion/react'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import SaveIcon from '@mui/icons-material/Save'
import TranslateIcon from '@mui/icons-material/Translate'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    commTypes,
    receiverTypes,
    SUPPORTED_LANGUAGES,
} from '../../helpers/Constants'
import AIService from '../../services/aiService'
import { variableExample } from '../../helpers/templateForm'
import { useToolRun } from '../../helpers/toolRun'
import useAiStatus from '../../hooks/useAiStatus'
import AiStatusGate from '../../components/shared/AiStatusGate'
import GlowButton from '../../components/shared/GlowButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { EmailFrame } from '../../components/TemplateRenderer'
import { emailPreviewHtml } from '../../helpers/emailPreview'
import {
    ACCENT,
    BRAND,
    CATEGORY,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEMPLATE_WRITER,
    TEXT,
    cardSx,
    menuProps,
    pageSx,
    tint,
} from '../../theme/tokens'

/* ── animations ─────────────────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`
const shimmerSweep = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`
const cursorBlink = keyframes`
  0%,100% { opacity:1; }
  50%      { opacity:0; }
`
const slideIn = keyframes`
  from { opacity:0; transform:translateX(20px); }
  to   { opacity:1; transform:translateX(0); }
`

/* ── palette (page accents derived from tokens) ──────────────────────────── */
const C = {
    border: SURFACE.border,
    accent: BRAND.orange,
    // Variables: the categorical cyan (text-safe on its tint).
    sky: CATEGORY.cyan,
    text: TEXT.primary,
    textDim: TEXT.muted,
    textMuted: TEXT.faint,
    green: STATUS.success.fg,
}

/* ── shared sx helpers ───────────────────────────────────────────────────── */
// The brand's Frames on TEMPLATE_WRITER.cardGround (the card ground).
const GLASS = { ...cardSx, background: TEMPLATE_WRITER.cardGround }
const INPUT_SX = {
    '& .MuiOutlinedInput-root': { typography: 'body2' },
}
const SELECT_SX = INPUT_SX
const LABEL = {
    typography: 'overline',
    display: 'block',
    color: C.textMuted,
    mb: 0.75,
}
// A code or message block inside a card: the sunken well.
const WELL_SX = {
    background: SURFACE.sunken,
    border: `1px solid ${C.border}`,
    borderRadius: `${RADIUS.sm}px`,
}

const CHANNELS = commTypes.filter((c) =>
    ['email', 'whatsapp', 'sms'].includes(c.value)
)

/* ── Typewriter ──────────────────────────────────────────────────────────── */
const Typewriter = ({ text, speed = 12 }) => {
    const [displayed, setDisplayed] = useState('')
    const [done, setDone] = useState(false)

    useEffect(() => {
        setDisplayed('')
        setDone(false)
        if (!text) return
        let i = 0
        const id = setInterval(() => {
            i++
            setDisplayed(text.slice(0, i))
            if (i >= text.length) {
                clearInterval(id)
                setDone(true)
            }
        }, speed)
        return () => clearInterval(id)
    }, [text, speed])

    return (
        <Box
            component="span"
            sx={{
                fontFamily: FONT.mono,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
            }}
        >
            {displayed}
            {!done && (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1em',
                        background: C.accent,
                        ml: '2px',
                        verticalAlign: 'text-bottom',
                        animation: `${cursorBlink} 0.8s ease-in-out infinite`,
                    }}
                />
            )}
        </Box>
    )
}

/* ── ShimmerBlock ────────────────────────────────────────────────────────── */
const ShimmerBlock = ({ width = '100%', height = 16 }) => (
    <Box
        sx={{
            width,
            height,
            borderRadius: `${RADIUS.xs}px`,
            background: `linear-gradient(90deg, ${ACCENT.bg04} 25%, ${ACCENT.bg15} 50%, ${ACCENT.bg04} 75%)`,
            backgroundSize: '200% 100%',
            animation: `${shimmerSweep} 1.6s ease-in-out infinite`,
        }}
    />
)

/* ── VarChip ─────────────────────────────────────────────────────────────── */
/** One variable of the generated template, in the syntax its channel substitutes. */
const VarChip = ({ v, channel }) => (
    <Box
        sx={{
            px: 1,
            py: 0.25,
            borderRadius: `${RADIUS.xs}px`,
            background: tint(C.sky, 0.1),
            border: `1px solid ${tint(C.sky, 0.3)}`,
            color: C.sky,
            typography: 'caption',
            fontFamily: FONT.mono,
        }}
    >
        {variableExample(channel, v)}
    </Box>
)

/* ── EmailBody ───────────────────────────────────────────────────────────── */
// The Preview / HTML toggle, in the Pipelines status toggles' style.
const VIEW_TOGGLE_SX = {
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        typography: 'caption',
        lineHeight: 1.4,
        px: 1.25,
        py: 0.25,
        color: TEMPLATE_WRITER.toggleText,
        borderColor: TEMPLATE_WRITER.toggleBorder,
    },
    '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
        {
            background: TEMPLATE_WRITER.toggleSelectedBg,
            color: TEMPLATE_WRITER.toggleSelectedText,
        },
}

/**
 * An email result as a recipient sees it: its HTML rendered in the Templates
 * preview's sandboxed frame (sanitised first, so no script, handler or
 * javascript: link survives), with ${variable} placeholders showing as
 * written. "HTML" shows the source instead. The Copy button beside it (the
 * `actions`) copies the source in either view.
 */
const EmailBody = ({ html, label, frameTitle, actions = null }) => {
    const [view, setView] = useState('preview')
    const safeHtml = useMemo(() => emailPreviewHtml(html), [html])
    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.75,
                }}
            >
                <Typography sx={{ ...LABEL, mb: 0, flex: 1 }}>
                    {label}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(e, next) => next && setView(next)}
                    aria-label={`${label}: preview or HTML`}
                    sx={VIEW_TOGGLE_SX}
                >
                    <ToggleButton value="preview">Preview</ToggleButton>
                    <ToggleButton value="html">HTML</ToggleButton>
                </ToggleButtonGroup>
                {actions}
            </Box>
            {view === 'preview' ? (
                <Box
                    sx={{
                        border: `1px solid ${C.border}`,
                        borderRadius: `${RADIUS.sm}px`,
                        overflow: 'hidden',
                        background: SURFACE.emailCanvas,
                    }}
                >
                    <EmailFrame
                        html={safeHtml}
                        minWidth={0}
                        startHeight={120}
                        title={frameTitle}
                    />
                </Box>
            ) : (
                <Box
                    component="pre"
                    aria-label={`${label} HTML`}
                    sx={{
                        ...WELL_SX,
                        p: 2.5,
                        m: 0,
                        typography: 'body2',
                        color: C.text,
                        fontFamily: FONT.mono,
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 480,
                        overflow: 'auto',
                    }}
                >
                    {html}
                </Box>
            )}
        </>
    )
}

/* ── main component ──────────────────────────────────────────────────────── */
const TemplateWriter = () => {
    const ai = useAiStatus()
    const navigate = useNavigate()
    const location = useLocation()
    const outputRef = useRef(null)
    const run = useToolRun('template-writer')

    const [description, setDescription] = useState('')
    const [channel, setChannel] = useState('whatsapp')
    const [receiverType, setReceiverType] = useState('batch_student')
    const [variablesRaw, setVariablesRaw] = useState('')
    const [generating, setGenerating] = useState(false)
    const [result, setResult] = useState(null)
    // The channel the shown result was generated for: the select can change
    // afterwards, and the chips must keep the syntax of the text they label.
    const [resultChannel, setResultChannel] = useState(channel)
    const [copied, setCopied] = useState(false)
    const [translateLang, setTranslateLang] = useState('Hindi')
    const [translating, setTranslating] = useState(false)
    const [translated, setTranslated] = useState(null)
    const [translatedCopied, setTranslatedCopied] = useState(false)
    const [error, setError] = useState('')
    const [translateError, setTranslateError] = useState('')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setTranslated(null)
        setError('')
        setTranslateError('')
        const saved = run.recall()
        if (saved) {
            setDescription(saved.description ?? '')
            setChannel(saved.channel)
            setReceiverType(saved.receiverType)
            setVariablesRaw(saved.variablesRaw ?? '')
            setResultChannel(saved.channel)
            setResult(saved.result)
            return
        }
        const state = location.state
        if (!state) return
        if (state.description) setDescription(state.description)
        if (state.channel) setChannel(state.channel)
    }, [location.state])

    useEffect(() => {
        if (result && outputRef.current) {
            setTimeout(
                () =>
                    outputRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    }),
                100
            )
        }
    }, [result])

    const handleGenerate = async () => {
        if (!description.trim()) return
        setGenerating(true)
        setError('')
        setTranslateError('')
        setResult(null)
        setTranslated(null)
        run.forget()
        try {
            const variables = variablesRaw
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean)
            const res = await AIService.generateTemplate({
                description: description.trim(),
                channel,
                receiver_type: receiverType,
                variables,
            })
            const generated = res.data?.result ?? null
            setResultChannel(channel)
            setResult(generated)
            if (generated)
                run.remember({
                    description,
                    channel,
                    receiverType,
                    variablesRaw,
                    result: generated,
                })
        } catch {
            setError('Failed to generate template. Please try again.')
        } finally {
            setGenerating(false)
        }
    }

    const handleTranslate = async () => {
        if (!result?.body) return
        setTranslating(true)
        setTranslateError('')
        setTranslated(null)
        try {
            const res = await AIService.translateTemplate({
                body: result.body,
                target_language: translateLang,
            })
            setTranslated(res.data?.translated ?? '')
        } catch {
            setTranslateError('Translation failed. Please try again.')
        } finally {
            setTranslating(false)
        }
    }

    const copy = (text, setter) => {
        navigator.clipboard.writeText(text)
        setter(true)
        setTimeout(() => setter(false), 1500)
    }

    // Checking, no key, or the check failed: one shared screen.
    if (ai.state !== 'ready') return <AiStatusGate status={ai} />

    // The translation's Copy, beside its label (and its Preview / HTML
    // toggle for an email): copies the translated text or HTML source.
    const translatedCopyButton = (
        <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: ICON.xs }} />}
            onClick={() => copy(translated, setTranslatedCopied)}
            sx={{
                typography: 'caption',
                color: translatedCopied ? C.green : C.textMuted,
                minWidth: 0,
                px: 1,
                '&:hover': { color: C.sky },
            }}
        >
            {translatedCopied ? 'Copied ✓' : 'Copy'}
        </Button>
    )

    /* ── main layout ── */
    return (
        <Box
            sx={{
                ...pageSx,
                /* glows and a subtle dot grid over the page */
                backgroundImage:
                    `radial-gradient(ellipse at 15% 30%, ${TEMPLATE_WRITER.glow} 0%, transparent 50%),` +
                    `radial-gradient(ellipse at 85% 70%, ${tint(
                        CATEGORY.cyan,
                        0.06
                    )} 0%, transparent 45%),` +
                    `radial-gradient(${tint(
                        BRAND.orange,
                        0.07
                    )} 1px, transparent 1px)`,
                backgroundSize: 'auto, auto, 28px 28px',
            }}
        >
            <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* ── page heading ── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1,
                        animation: `${fadeUp} 0.3s ease`,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: `${RADIUS.md}px`,
                            background: GRADIENT.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 0 16px ${ACCENT.border30}`,
                        }}
                    >
                        {/* Deep Blue on the brand gradient (white was 1.8:1 on its yellow). */}
                        <AutoAwesomeIcon
                            sx={{ fontSize: ICON.lg, color: ON_ORANGE }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ color: C.text, lineHeight: 1.2 }}
                        >
                            Template Writer
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: C.textMuted, mt: 0.25 }}
                        >
                            Describe what the message should do — AI writes it.
                        </Typography>
                    </Box>
                </Box>

                {/* ── config card ── */}
                <Box
                    sx={{
                        ...GLASS,
                        p: 3,
                        mb: 2.5,
                        animation: `${fadeUp} 0.35s ease`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            mb: 2.5,
                        }}
                    >
                        <Box>
                            <Typography sx={LABEL}>Channel</Typography>
                            <FormControl fullWidth sx={SELECT_SX}>
                                <Select
                                    value={channel}
                                    onChange={(e) => setChannel(e.target.value)}
                                    MenuProps={menuProps}
                                >
                                    {CHANNELS.map((c) => (
                                        <MenuItem key={c.value} value={c.value}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Typography sx={LABEL}>Receiver type</Typography>
                            <FormControl fullWidth sx={SELECT_SX}>
                                <Select
                                    value={receiverType}
                                    onChange={(e) =>
                                        setReceiverType(e.target.value)
                                    }
                                    MenuProps={menuProps}
                                >
                                    {receiverTypes.map((r) => (
                                        <MenuItem key={r.value} value={r.value}>
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={LABEL}>Variables</Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: C.textMuted, mb: 1 }}
                        >
                            Comma-separated — these become{' '}
                            <Box
                                component="span"
                                sx={{ color: C.sky, fontFamily: FONT.mono }}
                            >
                                {variableExample(channel, 'variable')}
                            </Box>{' '}
                            placeholders.
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="name, joining_link, session_time"
                            value={variablesRaw}
                            onChange={(e) => setVariablesRaw(e.target.value)}
                            sx={INPUT_SX}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography sx={LABEL}>Description</Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: C.textMuted, mb: 1 }}
                        >
                            Be specific — timing, tone, context, what action you
                            want the user to take.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder="e.g. Remind a batch student 2 hours before their Maths class. Warm, encouraging tone. Include teacher name and a CTA to join."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            sx={INPUT_SX}
                        />
                    </Box>

                    <GlowButton
                        loading={generating}
                        disabled={!description.trim()}
                        onClick={handleGenerate}
                    >
                        Generate template
                    </GlowButton>

                    {error && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={error} />
                        </Box>
                    )}
                </Box>

                {/* ── loading skeleton ── */}
                {generating && (
                    <Box
                        sx={{
                            ...GLASS,
                            p: 3,
                            animation: `${fadeUp} 0.3s ease`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2.5,
                            }}
                        >
                            <CircularProgress
                                size={14}
                                sx={{ color: C.accent }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: C.textDim }}
                            >
                                Writing template…
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            <ShimmerBlock width="75%" />
                            <ShimmerBlock width="90%" />
                            <ShimmerBlock width="60%" />
                            <ShimmerBlock width="80%" height={12} />
                        </Box>
                    </Box>
                )}

                {/* ── output card ── */}
                {result && !generating && (
                    <Box
                        ref={outputRef}
                        sx={{
                            ...GLASS,
                            overflow: 'hidden',
                            animation: `${slideIn} 0.4s ease`,
                        }}
                    >
                        {/* output header */}
                        <Box
                            sx={{
                                px: 3,
                                py: 1.75,
                                borderBottom: `1px solid ${C.border}`,
                                background: ACCENT.bg04,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: C.green,
                                        // A soft halo in the dot's own tint (a neon glow did not read
                                        // on the light theme).
                                        boxShadow: `0 0 0 3px ${STATUS.success.bg}`,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: C.text }}
                                >
                                    Generated
                                </Typography>
                                {result.char_count && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: C.textMuted, ml: 0.5 }}
                                    >
                                        · {result.char_count} chars
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                size="small"
                                startIcon={
                                    <ContentCopyIcon
                                        sx={{ fontSize: ICON.sm }}
                                    />
                                }
                                onClick={() => copy(result.body, setCopied)}
                                sx={{
                                    typography: 'caption',
                                    color: copied ? C.green : C.textDim,
                                    fontWeight: copied ? 600 : 400,
                                    minWidth: 0,
                                    px: 1,
                                    '&:hover': { color: ACCENT.text },
                                }}
                            >
                                {copied ? 'Copied ✓' : 'Copy'}
                            </Button>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            {result.subject && (
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={{ ...LABEL, mb: 0.5 }}>
                                        Subject
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: C.text }}
                                    >
                                        {result.subject}
                                    </Typography>
                                </Box>
                            )}

                            {/* An email is HTML: shown rendered, as it
                                arrives in an inbox (it printed its source). */}
                            {resultChannel === 'email' ? (
                                <EmailBody
                                    html={result.body ?? ''}
                                    label="Body"
                                    frameTitle="Email preview"
                                />
                            ) : (
                                <>
                                    <Typography sx={{ ...LABEL, mb: 0.75 }}>
                                        Body
                                    </Typography>
                                    <Box
                                        sx={{
                                            ...WELL_SX,
                                            p: 2.5,
                                            typography: 'body2',
                                            color: C.text,
                                            minHeight: 80,
                                        }}
                                    >
                                        <Typewriter text={result.body} />
                                    </Box>
                                </>
                            )}

                            {result.variables_used?.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: 'flex',
                                        gap: 0.75,
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: C.textMuted }}
                                    >
                                        Variables used:
                                    </Typography>
                                    {result.variables_used.map((v) => (
                                        <VarChip
                                            key={v}
                                            v={v}
                                            channel={resultChannel}
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* ── integration CTAs ── */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mt: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={() =>
                                        navigate('/create-template', {
                                            state: {
                                                body: result.body,
                                                channel: resultChannel,
                                                subject: result.subject || '',
                                            },
                                        })
                                    }
                                >
                                    Save as Template
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<GppMaybeIcon />}
                                    onClick={() =>
                                        navigate('/ai/unsubscribe-risk', {
                                            state: {
                                                template_body: result.body,
                                                channel: resultChannel,
                                            },
                                        })
                                    }
                                >
                                    Check Risk
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() =>
                                        navigate('/ai/bulk-preview', {
                                            state: {
                                                template_body: result.body,
                                                channel: resultChannel,
                                            },
                                        })
                                    }
                                >
                                    Preview Messages
                                </Button>
                            </Box>
                        </Box>

                        {/* translate strip */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                borderTop: `1px solid ${C.border}`,
                                background: SURFACE.subtle,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                            }}
                        >
                            <TranslateIcon
                                sx={{ fontSize: ICON.md, color: C.textMuted }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: C.textDim }}
                            >
                                Translate to
                            </Typography>
                            <FormControl
                                size="small"
                                sx={{ minWidth: 130, ...SELECT_SX }}
                            >
                                <Select
                                    value={translateLang}
                                    onChange={(e) => {
                                        setTranslateLang(e.target.value)
                                        setTranslated(null)
                                    }}
                                    MenuProps={menuProps}
                                >
                                    {SUPPORTED_LANGUAGES.map((l) => (
                                        <MenuItem key={l.value} value={l.value}>
                                            {l.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                disabled={translating}
                                onClick={handleTranslate}
                            >
                                {translating ? (
                                    <CircularProgress
                                        size={13}
                                        sx={{ color: C.accent }}
                                    />
                                ) : (
                                    'Translate'
                                )}
                            </Button>
                        </Box>

                        {translateError && (
                            <Box sx={{ px: 3, pb: 2 }}>
                                <ErrorBanner message={translateError} />
                            </Box>
                        )}

                        {/* translated output */}
                        {translating && (
                            <Box sx={{ px: 3, pb: 2.5 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <CircularProgress
                                        size={12}
                                        sx={{ color: C.sky }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ color: C.textDim }}
                                    >
                                        Translating…
                                    </Typography>
                                </Box>
                                <ShimmerBlock width="70%" />
                            </Box>
                        )}

                        {translated && !translating && (
                            <Box
                                sx={{
                                    px: 3,
                                    pb: 3,
                                    animation: `${slideIn} 0.35s ease`,
                                }}
                            >
                                {resultChannel === 'email' ? (
                                    <EmailBody
                                        html={translated}
                                        label={translateLang}
                                        frameTitle="Translated email preview"
                                        actions={translatedCopyButton}
                                    />
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 0.75,
                                            }}
                                        >
                                            <Typography sx={{ ...LABEL }}>
                                                {translateLang}
                                            </Typography>
                                            {translatedCopyButton}
                                        </Box>
                                        <Box
                                            sx={{
                                                background: tint(C.sky, 0.05),
                                                border: `1px solid ${tint(
                                                    C.sky,
                                                    0.25
                                                )}`,
                                                borderRadius: `${RADIUS.sm}px`,
                                                p: 2.5,
                                                typography: 'body2',
                                                color: C.text,
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: 1.7,
                                                fontFamily: FONT.mono,
                                            }}
                                        >
                                            <Typewriter
                                                text={translated}
                                                speed={8}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default TemplateWriter
